import { Check } from "lucide-react";
import { Reveal, SectionHead, Avatar } from "../components/bits";

const STAGES = [
    { label: "Client visits", sub: "Day 0" },
    { label: "Expected return window", sub: "~12 months" },
    { label: "No new booking", sub: "Overdue" },
    { label: "Reactivation triggered", sub: "Automatic" },
    { label: "Follow-up", sub: "SMS sent" },
    { label: "Rebooked ✓", sub: "Confirmed" },
];

export const OngoingSystem = () => (
    <section data-testid="ongoing-system" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <SectionHead
                eyebrow="The ongoing system"
                title="It keeps an eye on who should be coming back."
                copy="Instead of waiting until your database is full of completely dormant clients, the system can continually catch people who are becoming overdue and follow up at the right time."
            />

            <Reveal delay={0.15} className="mt-12">
                <div className="rounded-3xl border border-linen bg-ivory p-6 sm:p-10">
                    <div className="relative">
                        <div className="absolute left-0 right-0 top-[26px] h-px">
                            <svg className="h-2 w-full" preserveAspectRatio="none">
                                <line x1="0" y1="4" x2="100%" y2="4" stroke="#D8A7A1" strokeOpacity="0.5" strokeWidth="1.2" className="line-flow" />
                            </svg>
                        </div>
                        <div className="absolute left-0 right-0 top-[22px] hidden h-3 sm:block">
                            <span
                                className="absolute flex h-2.5 w-2.5 items-center justify-center"
                                style={{ animation: "travelX 9s linear infinite" }}
                            >
                                <span className="h-2.5 w-2.5 rounded-full bg-gold shadow-[0_0_12px_rgba(203,163,104,0.7)]" />
                            </span>
                        </div>
                        <div className="relative grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-6">
                            {STAGES.map((s, i) => (
                                <div key={s.label} className="flex flex-col items-center text-center">
                                    <span
                                        className={`z-10 flex h-[52px] w-[52px] items-center justify-center rounded-full border-2 bg-ivory font-mono text-[10px] tracking-widest ${
                                            i === 5
                                                ? "border-gold text-gold"
                                                : i === 3
                                                  ? "animate-glow border-rose text-rose"
                                                  : "border-linen text-faint"
                                        }`}
                                    >
                                        {i === 5 ? <Check className="h-4 w-4" strokeWidth={2.5} /> : String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div className="mt-3 font-sans text-[12px] font-medium uppercase leading-tight tracking-[0.08em] text-ink">
                                        {s.label}
                                    </div>
                                    <div className="mt-1 font-mono text-[8px] uppercase tracking-[0.2em] text-faint">{s.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative mt-10 hidden h-16 sm:block">
                        <div
                            className="absolute top-0 flex items-center gap-2.5 rounded-full border border-linen bg-white py-2 pl-2 pr-4 shadow-[0_10px_30px_rgba(17,17,19,0.08)]"
                            style={{ animation: "travelX 9s linear infinite" }}
                        >
                            <Avatar initials="S" tone="bg-blush text-ink" />
                            <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-ink">Sarah · Lip filler</span>
                        </div>
                    </div>

                    <div className="mt-4 flex flex-col items-center justify-between gap-4 rounded-2xl bg-sand px-6 py-5 sm:flex-row">
                        <div className="flex items-center gap-3">
                            <div className="grid grid-cols-7 gap-[3px]">
                                {Array.from({ length: 14 }).map((_, i) => (
                                    <span key={i} className={`h-2.5 w-2.5 rounded-[3px] ${i === 11 ? "animate-pulse-soft bg-rose" : "bg-ink/10"}`} />
                                ))}
                            </div>
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-mute">Calendar moves forward</span>
                        </div>
                        <span className="font-serif text-lg italic text-ink">…the moment a client becomes overdue, the system notices.</span>
                    </div>
                </div>
            </Reveal>

            <Reveal delay={0.2} className="mt-10 text-center">
                <p className="mx-auto max-w-2xl font-serif text-xl uppercase leading-snug tracking-tight text-ink sm:text-2xl">
                    One system. Running in the background. Continually creating opportunities from clients you{" "}
                    <span className="italic text-rose">already have.</span>
                </p>
            </Reveal>
        </div>
    </section>
);
